import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { BarChart3, Sparkles, ArrowRight } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import api from "@/lib/api";
import { Header } from "@/pages/CollegePredictorPage";
import { Stat, Crumb, err, startTest, trackEvent } from "./shared";

export default function PrepPerformance() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [targets, setTargets] = useState(null);
  const [perf, setPerf] = useState(null);
  const examId = params.get("exam") || targets?.[0]?.id;

  useEffect(() => { api.get("/test-prep/targets").then((r) => setTargets(r.data.targets)).catch((e) => toast.error(err(e))); }, []);
  useEffect(() => {
    if (!examId) return;
    setPerf(null);
    api.get(`/test-prep/performance?exam_id=${examId}`).then((r) => setPerf(r.data)).catch((e) => toast.error(err(e)));
  }, [examId]);

  if (!targets) return <div className="p-8 text-slate-400">Loading...</div>;

  return (
    <div data-testid="prep-performance" className="mx-auto max-w-6xl px-4 sm:px-8 py-8">
      <Crumb items={[{ to: "/prep", label: "Test Preparation" }, { label: "My Performance" }]} />
      <Header icon={BarChart3} eyebrow="My Preparation" title="My Performance" desc="Deterministic analysis of every submitted test — accuracy, trend, strong and weak areas." />
      {targets.length === 0 ? (
        <p className="text-sm text-slate-400">No preparation target yet. <Link to="/prep" className="text-cyan-300">Choose an exam</Link> to begin.</p>
      ) : (
        <>
          <div className="mb-6 flex flex-wrap gap-2">
            {targets.map((t) => <button key={t.id} data-testid={`perf-exam-${t.slug}`} onClick={() => setParams({ exam: t.id })} className={`rounded-full px-4 py-1.5 text-xs font-semibold ${t.id === examId ? "bg-cyan-400 text-[#041016]" : "bg-white/5 text-slate-400 hover:text-white"}`}>{t.name}</button>)}
          </div>
          {!perf ? <p className="text-sm text-slate-500">Loading...</p> : (
            <>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
                <Stat label="Tests taken" value={perf.tests_taken} testid="perf-tests-taken" />
                <Stat label="Mocks taken" value={perf.mocks_taken} tone="rose" testid="perf-mocks" />
                <Stat label="Questions attempted" value={perf.questions_attempted} tone="indigo" testid="perf-questions" />
                <Stat label="Average score" value={perf.avg_score} tone="emerald" testid="perf-avg-score" />
                <Stat label="Accuracy" value={`${perf.avg_accuracy}%`} tone="amber" testid="perf-accuracy" />
              </div>

              <div className="mt-6 grid gap-6 lg:grid-cols-3">
                <div className="rounded-2xl border border-white/8 bg-[#131C2E] p-5 lg:col-span-2">
                  <h3 className="font-semibold">Score trend (% of total marks)</h3>
                  {perf.trend.length < 2 ? <p className="mt-3 text-sm text-slate-500">Take at least two tests to see a trend.</p> : (
                    <div className="mt-3" style={{ width: "100%", height: 220 }}>
                      <ResponsiveContainer>
                        <AreaChart data={perf.trend.map((t, i) => ({ ...t, n: i + 1 }))}>
                          <defs><linearGradient id="tp" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#06B6D4" stopOpacity={0.5} /><stop offset="100%" stopColor="#06B6D4" stopOpacity={0} /></linearGradient></defs>
                          <XAxis dataKey="n" tick={{ fill: "#64748b", fontSize: 11 }} /><YAxis domain={[0, 100]} tick={{ fill: "#64748b", fontSize: 11 }} />
                          <Tooltip contentStyle={{ background: "#0F1626", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} formatter={(v, n, p) => [`${v}%`, p.payload.title]} />
                          <Area type="monotone" dataKey="percentage" stroke="#06B6D4" fill="url(#tp)" strokeWidth={2} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  <h3 className="mt-6 font-semibold">Accuracy by subject</h3>
                  <div className="mt-2 space-y-2">
                    {perf.by_subject.filter((s) => s.attempted).map((s) => (
                      <div key={s.id} className="text-sm"><div className="flex justify-between"><span>{s.name}</span><span className="font-mono-data text-xs text-slate-400">{s.correct}/{s.attempted} · {s.accuracy}%</span></div>
                        <div className="mt-1 h-1.5 rounded-full bg-white/5"><div className={`h-full rounded-full ${s.accuracy >= 65 ? "bg-emerald-400" : "bg-rose-400"}`} style={{ width: `${s.accuracy}%` }} /></div></div>
                    ))}
                    {!perf.by_subject.some((s) => s.attempted) && <p className="text-sm text-slate-500">No attempted questions yet.</p>}
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="rounded-2xl border border-white/8 bg-[#131C2E] p-5">
                    <h3 className="font-semibold">Strong topics</h3>
                    <List rows={perf.strong_topics} tone="emerald" empty="None yet (≥75% accuracy, 3+ questions)." />
                    <h3 className="mt-4 font-semibold">Weak topics</h3>
                    <List rows={perf.weak_topics} tone="rose" empty="None detected (<50% accuracy, 3+ questions)." />
                  </div>
                  <div className="rounded-2xl border border-cyan-400/25 bg-gradient-to-br from-[#0F2A33] to-[#131C2E] p-5">
                    <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-cyan-400" /><h3 className="font-semibold">Recommended tests</h3></div>
                    <div className="mt-3 space-y-2">
                      {perf.recommendations.map((r) => (
                        <button key={r.test_id} data-testid={`perf-rec-${r.test_type}`} onClick={() => { trackEvent("recommendation_clicked", { test_id: r.test_id, exam_id: examId }); startTest(navigate, r.test_id, true); }}
                          className="w-full rounded-xl border border-white/10 bg-white/[0.03] p-3 text-left hover:border-cyan-400/40">
                          <p className="text-sm font-semibold">{r.title}</p><p className="mt-0.5 text-xs text-slate-400">{r.reason}</p>
                          <p className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-cyan-300">Start <ArrowRight className="h-3 w-3" /></p>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

const List = ({ rows, tone, empty }) => rows.length ? (
  <ul className="mt-2 space-y-1 text-sm">{rows.map((t) => <li key={t.id} className="flex justify-between"><span>{t.name}</span><span className={`font-mono-data text-${tone}-300`}>{t.accuracy}%</span></li>)}</ul>
) : <p className="mt-1 text-xs text-slate-500">{empty}</p>;
